import type { DistribuicaoMensal, Parametros } from '@favo/core';

import { Celula } from '@/componentes/celula';
import { Valor } from '@/componentes/valor';
import { formatarMoeda } from '@/design/formato';

import { faltaPreencher, fraseEquilibrio, MES, motivoCurvaUniforme } from './frases';
import { GraficoEquilibrio, GraficoVolume } from './graficos';
import { formatarVolume, tomDoLucro } from './grandezas';

/** Os dois gráficos, a prosa do equilíbrio e a tabela dos doze meses. */
export function Curva({
  p,
  dist,
  distSugerido,
}: {
  p: Parametros;
  dist: DistribuicaoMensal | null;
  distSugerido: DistribuicaoMensal | null;
}) {
  return (
    <Celula como="section" className="secao">
      <h2 className="titulo-cartao titulo-secao">Distribuição mensal e ponto de equilíbrio</h2>

      {!dist ? (
        <p className="apoio">{faltaPreencher(p)}</p>
      ) : (
        <>
          <div className="graficos">
            <figure className="grafico">
              <figcaption className="rotulo">Projetos por mês</figcaption>
              <GraficoVolume dist={dist} />
            </figure>
            <figure className="grafico">
              <figcaption className="rotulo">
                Margem acumulada contra a despesa fixa do ano
              </figcaption>
              <GraficoEquilibrio dist={dist} distSugerido={distSugerido} />
              <div className="legenda">
                <span>
                  <i className="traco traco-atual" aria-hidden="true" /> Margem acumulada, preço
                  atual
                </span>
                {distSugerido ? (
                  <span>
                    <i className="traco traco-minimo" aria-hidden="true" /> Margem acumulada, preço
                    no mínimo
                  </span>
                ) : null}
                <span>
                  <i className="traco traco-fixa" aria-hidden="true" /> Despesa fixa do ano,{' '}
                  {formatarMoeda(dist.fixasAno)}
                </span>
              </div>
            </figure>
          </div>

          <p className="prosa mt-5">
            {fraseEquilibrio(dist, 'ao preço atual')}
            {distSugerido ? ` ${fraseEquilibrio(distSugerido, 'com o preço no mínimo')}` : ''}
          </p>

          {dist.uniforme ? (
            <p className="apoio-nota mt-2">
              A curva mensal é uma estimativa: {motivoCurvaUniforme(p)} Informe doze meses de
              sazonalidade para ver a forma real do ano.
            </p>
          ) : null}

          <div className="tabela-meses">
            <table>
              <thead>
                <tr>
                  <th scope="col">Mês</th>
                  <th scope="col">Projetos</th>
                  <th scope="col">Faturamento</th>
                  <th scope="col">Margem de contribuição</th>
                  <th scope="col">Margem acumulada</th>
                </tr>
              </thead>
              <tbody>
                {dist.linhas.map((l) => (
                  <tr
                    key={l.mes}
                    {...(l.mes === dist.mesEquilibrio ? { 'data-equilibrio': '' } : {})}
                  >
                    <th scope="row">{MES[l.mes]}</th>
                    <td>
                      <span className="numero">{formatarVolume(l.projetos)}</span>
                    </td>
                    <td>
                      <Valor valor={l.faturamento} formato="moeda" />
                    </td>
                    <td>
                      <Valor valor={l.mc} formato="moeda" tom={tomDoLucro(l.mc)} />
                    </td>
                    <td>
                      <Valor valor={l.mcAcumulada} formato="moeda" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Celula>
  );
}
